// Browser-side video processing for the Promote tab.
// Uses @ffmpeg/ffmpeg (WASM) for scene detection, clip extraction, and reel
// assembly. The user's source files never leave the browser.
//
// Public API:
//   probeVideo(blob) -> { duration }
//   pickRandomWindows(duration, count, clipSec, minGapSec) -> [{start, end}]
//   extractClip(blob, { start, duration, outputName }) -> Blob (mp4)
//   assembleReel({ clipBlobs, captions, ctaCaption, fps, outputName }) -> Blob
//
// Initialisation is lazy — the WASM core (~30MB) is only downloaded the first
// time the user clicks "Generate Reels". Subsequent calls reuse the same
// instance until the page is reloaded.

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

let ffmpegInstance = null;
let loadingPromise = null;

// Load ffmpeg-core from the SAME ORIGIN (public/ffmpeg/) so we never depend on
// unpkg/jsdelivr being reachable — those CDNs are commonly blocked by strict
// production CSPs (which is exactly what was triggering "Failed to fetch" on
// the deployed domain). If, for any reason, the same-origin files 404, we fall
// back to the canonical unpkg URL so dev environments without the public file
// keep working.
const LOCAL_CORE_BASE = "/ffmpeg";
const CDN_CORE_BASE   = "https://unpkg.com/@ffmpeg/[email protected]/dist/umd";

async function loadCoreUrls(base) {
  // toBlobURL fetches and converts to a same-origin blob URL — required by
  // ffmpeg.wasm because the multi-threaded worker is loaded via importScripts.
  return {
    coreURL: await toBlobURL(`${base}/ffmpeg-core.js`,   "text/javascript"),
    wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, "application/wasm"),
  };
}

// ─────────────────────────────────────────────────────────────────────
// FFmpeg log tail — a ring buffer that captures the last N log lines from
// the ffmpeg worker. When any ffmpeg.exec / assembleReel call fails we splice
// these into the thrown error so the user sees the REAL cause instead of
// the dreaded "Reel 1 failed: unknown".
// ─────────────────────────────────────────────────────────────────────
const FFMPEG_LOG_TAIL_MAX = 30;
const ffmpegLogTail = [];

function pushFfmpegLog(line) {
  if (!line) return;
  ffmpegLogTail.push(String(line).slice(0, 500));
  if (ffmpegLogTail.length > FFMPEG_LOG_TAIL_MAX) ffmpegLogTail.shift();
}

export function getRecentFFmpegLogs() {
  return ffmpegLogTail.slice();
}

export function clearFFmpegLogs() {
  ffmpegLogTail.length = 0;
}

// Builds a rich error from an underlying exception. The message includes
// the original error text PLUS the last few ffmpeg log lines that the
// worker emitted just before crashing — that's where the real cause lives.
function enrichError(label, err) {
  const original = err?.message || err?.toString?.() || String(err) || "(empty)";
  const tail = ffmpegLogTail.slice(-6).join(" | ").trim();
  const detail = tail ? ` · ffmpeg: ${tail}` : "";
  const out = new Error(`${label}: ${original}${detail}`);
  out.cause = err;
  out.ffmpegLog = ffmpegLogTail.slice();
  return out;
}

export async function getFFmpeg(onLog) {
  if (ffmpegInstance?.loaded) return ffmpegInstance;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const ff = new FFmpeg();
    ff.on("log", ({ message }) => {
      pushFfmpegLog(message);
      if (onLog) onLog(message);
    });

    // 1) Try same-origin first (production-safe under strict CSP).
    let urls;
    try {
      urls = await loadCoreUrls(LOCAL_CORE_BASE);
    } catch (localErr) {
      console.warn("[ffmpeg] local core load failed, falling back to CDN:", localErr?.message || localErr);
      // 2) Fallback to unpkg (works in permissive dev environments).
      urls = await loadCoreUrls(CDN_CORE_BASE);
    }
    try {
      await ff.load(urls);
    } catch (loadErr) {
      throw enrichError("ffmpeg core load failed", loadErr);
    }
    ffmpegInstance = ff;
    return ff;
  })().catch((err) => {
    // Reset so the next click can retry; otherwise the cached rejected promise
    // poisons every subsequent attempt.
    loadingPromise = null;
    throw err;
  });

  return loadingPromise;
}

// ─────────────────────────────────────────────────────────────────────
// VIDEO PROBE — duration only
// We don't need scene boundaries. Travel B-roll is intentionally random;
// reviews footage is a single talking-head clip. Both just need a duration
// so we can pick random 3-second windows from inside them. The HTML5
// <video> element gives us that virtually instantly without spinning up
// ffmpeg, which fixes the "Couldn't find any usable scenes" failure.
// ─────────────────────────────────────────────────────────────────────
export async function probeVideo(blob) {
  if (!blob) throw new Error("Empty video");
  const url = URL.createObjectURL(blob);
  try {
    const duration = await new Promise((resolve, reject) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.src = url;
      const timer = setTimeout(() => reject(new Error("Couldn't read video metadata (timed out)")), 8000);
      v.onloadedmetadata = () => {
        clearTimeout(timer);
        const d = Number(v.duration);
        if (!Number.isFinite(d) || d <= 0) {
          reject(new Error("Video appears to be corrupted or unreadable"));
        } else {
          resolve(d);
        }
      };
      v.onerror = () => {
        clearTimeout(timer);
        reject(new Error("Couldn't open video — file may be corrupted"));
      };
    });
    return { duration };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Pick `count` random non-overlapping start timestamps (seconds) inside a
// video of `duration` seconds. Each window is `clipSec` long and adjacent
// picks are kept at least `minGapSec` apart for visual variety.
//
// If the video isn't long enough for the requested spacing, we relax the
// gap progressively rather than throw. Only true hard-failure is when the
// video itself is shorter than ONE clipSec — then there's nothing we can
// possibly extract.
export function pickRandomWindows(duration, count, clipSec = 3.0, minGapSec = 7.0) {
  if (!Number.isFinite(duration) || duration < clipSec) {
    throw new Error(`Video is too short (need at least ${clipSec}s, got ${duration?.toFixed?.(2) || 0}s)`);
  }

  // Available range for start timestamps.
  const maxStart = Math.max(0, duration - clipSec);

  // Strategy: try a reasonable gap, retry with smaller gaps if we can't fit
  // `count` non-overlapping windows. Final fallback: any clipSec-spaced
  // start is allowed (just no exact overlap).
  const gaps = [minGapSec, minGapSec * 0.5, minGapSec * 0.25, clipSec];
  for (const gap of gaps) {
    const picks = [];
    const MAX_TRIES = 80;
    for (let attempt = 0; attempt < MAX_TRIES && picks.length < count; attempt++) {
      const start = Math.random() * maxStart;
      const tooClose = picks.some((p) => Math.abs(p - start) < gap);
      if (!tooClose) picks.push(start);
    }
    if (picks.length >= count) {
      picks.sort((a, b) => a - b);
      return picks.slice(0, count).map((s) => ({ start: s, end: s + clipSec }));
    }
  }

  // Ultimate fallback: video really is small — pack the windows tip-to-tail
  // with whatever gap the duration allows. We'll happily repeat starts so
  // the reel still completes; the user uploaded short footage, that's on them.
  const windows = [];
  for (let i = 0; i < count; i++) {
    const start = Math.min(maxStart, (i * (duration / count)));
    windows.push({ start, end: start + clipSec });
  }
  return windows;
}

// ─────────────────────────────────────────────────────────────────────
// CLIP EXTRACTION
// Extracts a single [start, start+duration] slice from the source, re-encodes
// to a normalised 1080x1920 vertical mp4 so all extracted clips concatenate
// cleanly. Drops audio (reel audio is added by the user in CapCut/Reels).
// ─────────────────────────────────────────────────────────────────────
export async function extractClip(blob, { start, duration, outputName = "clip.mp4", fps = 30, padToDuration = 0 }) {
  const ff = await getFFmpeg();
  const inputName = `in_${Math.random().toString(36).slice(2, 8)}.mp4`;
  // If padToDuration is greater than the source slice we asked for, clone the
  // last frame to hold for the remainder. This is how Scene 4 (CTA) reaches
  // a full 5s read-time even when the user's REVIEWS footage is short — the
  // person + book stay on screen long enough for the viewer to process the
  // social proof and decide to comment.
  const needsPad = padToDuration && padToDuration > duration + 0.01;
  const padSecs = needsPad ? (padToDuration - duration) : 0;
  const outDur = needsPad ? padToDuration : duration;
  const filterParts = [
    `scale=1080:1920:force_original_aspect_ratio=increase`,
    `crop=1080:1920`,
    `fps=${fps}`,
    `setsar=1`,
  ];
  if (needsPad) filterParts.push(`tpad=stop_mode=clone:stop_duration=${padSecs.toFixed(3)}`);
  try {
    await ff.writeFile(inputName, await fetchFile(blob));

    await ff.exec([
      "-ss", String(start),
      "-t", String(duration),
      "-i", inputName,
      "-vf",
      // Cover-fit to 1080x1920 (center-crop), enforce 30fps, no audio.
      // If we need to extend (padToDuration), tpad clones the last frame.
      filterParts.join(","),
      "-frames:v", String(Math.round(outDur * fps)),
      "-an",
      "-c:v", "libx264",
      "-preset", "veryfast",
      "-crf", "23",
      "-pix_fmt", "yuv420p",
      "-movflags", "+faststart",
      outputName,
    ]);

    const data = await ff.readFile(outputName);
    try { await ff.deleteFile(inputName); } catch { /* ignore */ }
    try { await ff.deleteFile(outputName); } catch { /* ignore */ }
    return new Blob([data.buffer], { type: "video/mp4" });
  } catch (e) {
    // Cleanup attempts ignore-errors — file may not exist if exec died early.
    try { await ff.deleteFile(inputName); } catch { /* */ }
    try { await ff.deleteFile(outputName); } catch { /* */ }
    throw enrichError(`extractClip failed (start=${start.toFixed(2)}s, dur=${duration.toFixed(2)}s)`, e);
  }
}

// ─────────────────────────────────────────────────────────────────────
// REEL ASSEMBLY
// Concatenates a sequence of pre-normalised clips and burns captions onto
// each one via ffmpeg's `drawtext` filter. CapCut-style overlay: white
// rounded rectangle (drawn via `drawbox` with `boxcolor`), black text,
// no shadows. We render to a single mp4 at 1080x1920 30fps.
// ─────────────────────────────────────────────────────────────────────

// Escape user text for ffmpeg's drawtext filter (single quotes, colons, percent, backslash).
function escapeDrawText(t) {
  return String(t || "")
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/:/g, "\\:")
    .replace(/%/g, "\\%");
}

// Wrap a long caption into ≤4 short lines for the white-pill caption style.
function wrapCaption(text, perLine = 18) {
  const words = String(text || "").trim().split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > perLine && cur) { lines.push(cur); cur = w; }
    else cur = (cur ? cur + " " : "") + w;
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 4).join("\n");
}

export async function assembleReel({
  clipBlobs,           // array of pre-normalised mp4 Blobs (1080x1920, 30fps, no audio)
  captions,            // array of strings, one per clip — burned as CapCut-style overlays
  ctaTopMessage = "",  // OPTIONAL: short aspirational benefit drawn at the TOP of the last clip (Scene 4)
  outputName = "reel.mp4",
  fps = 30,
}) {
  if (!Array.isArray(clipBlobs) || clipBlobs.length === 0) throw new Error("No clips supplied");

  const ff = await getFFmpeg();
  const localNames = [];
  const burnedNames = [];
  const lastIdx = clipBlobs.length - 1;  // Scene 4 (CTA scene) gets the dual-text layout.

  try {
    for (let i = 0; i < clipBlobs.length; i++) {
      const n = `c${i}.mp4`;
      await ff.writeFile(n, await fetchFile(clipBlobs[i]));
      localNames.push(n);
    }

    // 1) Burn caption on each clip with the white-pill / black-text style.
    const hasFont = await ensureFont(ff);
    for (let i = 0; i < localNames.length; i++) {
      const burned = `b${i}.mp4`;
      const wrapped = wrapCaption(captions?.[i] || "");
      const safe = escapeDrawText(wrapped);
      const isCtaScene = (i === lastIdx);

      // For Scenes 1-3: single caption positioned in the UPPER-MIDDLE band of
      // the frame (~y=42% of height) — where viewers naturally focus on Reels
      // / Shorts and AWAY from the platform UI at the bottom. Previously the
      // captions sat at y=72% (below centre) which both fought the platform
      // engagement buttons AND drew the eye away from the action.
      // For Scene 4 (CTA): a DUAL-TEXT layout —
      //   · ctaTopMessage at y≈18% — short aspirational benefit (the WHY)
      //   · main CTA caption at y≈80% — the comment-driven ask (the HOW)
      // The middle band stays clear so the review-clip's subject (person
      // holding the book) is fully visible — social proof in the centre.
      let drawTextFilter = "null";
      if (wrapped && hasFont) {
        if (isCtaScene) {
          const topMsg = (ctaTopMessage || "").trim();
          const topSafe = escapeDrawText(wrapCaption(topMsg, 26));
          // Two stacked drawtext filters: smaller top benefit, larger bottom CTA.
          const topDraw = topSafe
            ? `drawtext=text='${topSafe}':fontfile=/tmp/font.ttf:fontsize=54:fontcolor=black` +
              `:box=1:boxcolor=white@0.96:boxborderw=22` +
              `:x=(w-text_w)/2:y=h*0.16` +
              `:line_spacing=10`
            : null;
          const bottomDraw = `drawtext=text='${safe}':fontfile=/tmp/font.ttf:fontsize=70:fontcolor=black` +
            `:box=1:boxcolor=white@0.97:boxborderw=26` +
            `:x=(w-text_w)/2:y=h*0.78-text_h/2` +
            `:line_spacing=12`;
          drawTextFilter = [topDraw, bottomDraw].filter(Boolean).join(",");
        } else {
          drawTextFilter =
            `drawtext=text='${safe}':fontfile=/tmp/font.ttf:fontsize=64:fontcolor=black` +
            `:box=1:boxcolor=white@0.96:boxborderw=24` +
            `:x=(w-text_w)/2:y=h*0.42-text_h/2` +
            `:line_spacing=12`;
        }
      }

      await ff.exec([
        "-i", localNames[i],
        "-vf", drawTextFilter,
        "-c:v", "libx264",
        "-preset", "veryfast",
        "-crf", "20",
        "-pix_fmt", "yuv420p",
        "-an",
        "-movflags", "+faststart",
        burned,
      ]);

      // Validate the burned file actually exists + has content. If drawtext
      // (or any other filter step) silently produced an empty / partial output
      // the concat step downstream would die with the confusing "moov atom
      // not found" + "Impossible to open b0.mp4" error chain. Surfacing the
      // root cause HERE saves another debug round-trip.
      let burnedSize = 0;
      try {
        const stat = await ff.readFile(burned);
        burnedSize = stat?.byteLength || stat?.length || 0;
      } catch (statErr) {
        throw new Error(`burn step ${i} produced no output (${statErr?.message || "missing file"})`);
      }
      if (burnedSize < 2048) {
        throw new Error(`burn step ${i} produced empty file (${burnedSize}B) — drawtext filter likely rejected`);
      }
      burnedNames.push(burned);
    }

    // 2) Concat the burned clips via concat demuxer.
    const listText = burnedNames.map((n) => `file '${n}'`).join("\n");
    await ff.writeFile("concat.txt", new TextEncoder().encode(listText));

    await ff.exec([
      "-f", "concat",
      "-safe", "0",
      "-i", "concat.txt",
      "-c", "copy",
      "-movflags", "+faststart",
      outputName,
    ]);

    const data = await ff.readFile(outputName);

    // 3) Cleanup
    for (const n of [...localNames, ...burnedNames, "concat.txt", outputName]) {
      try { await ff.deleteFile(n); } catch (_) { /* */ }
    }

    return new Blob([data.buffer], { type: "video/mp4" });
  } catch (e) {
    // Best-effort cleanup so the next reel doesn't fight over orphaned files.
    for (const n of [...localNames, ...burnedNames, "concat.txt", outputName]) {
      try { await ff.deleteFile(n); } catch (_) { /* */ }
    }
    throw enrichError("assembleReel failed", e);
  }
}

// ─────────────────────────────────────────────────────────────────────
// FONT — ffmpeg.wasm has no fonts of its own. We ship Inter Bold from the
// same origin (/ffmpeg/inter-bold.ttf) — the CDN was blocked by strict
// production CSP, which silently broke caption rendering and surfaced as
// the dreaded "Reel 1 failed: unknown" message.
// ─────────────────────────────────────────────────────────────────────
let fontLoaded = false;
const FONT_LOCAL = "/ffmpeg/inter-bold.ttf";
const FONT_CDN_FALLBACK = "https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-700-normal.ttf";

async function ensureFont(ff) {
  if (fontLoaded) return true;
  for (const url of [FONT_LOCAL, FONT_CDN_FALLBACK]) {
    try {
      const r = await fetch(url);
      if (!r.ok) continue;
      const buf = await r.arrayBuffer();
      if (!buf || buf.byteLength < 1024) continue;
      await ff.writeFile("/tmp/font.ttf", new Uint8Array(buf));
      fontLoaded = true;
      return true;
    } catch (_) { /* try next */ }
  }
  // Capture in the ffmpeg log tail so the enrichError surface mentions it.
  pushFfmpegLog("Font fetch failed (both /ffmpeg/inter-bold.ttf and CDN). Captions will be skipped.");
  return false;
}
